import type {
  OnboardingItem,
  ProductionCommentAccess,
  ProjectTask,
  RfqProject,
  RfqSubmission,
  SupplierWorkFlag,
  TimberProject,
  VendorCapability,
  VendorWorkType,
} from '../types';
import { buildGroupCommentKey } from './groupComments';
import { isProductionGroupName } from './paymentDueFlags';
import {
  parseSupplierWorkFlags,
  supplierWorkFlagsFromOrgCapabilities,
} from './supplierWorkFlags';

export const PRODUCTION_COMMENT_GROUP = 'production';

export function productionCommentAccessId(dealId: string): string {
  return buildGroupCommentKey(dealId, PRODUCTION_COMMENT_GROUP);
}

export function dealIdFromProductionCommentKey(groupCommentKey: string | undefined): string {
  if (!groupCommentKey) return '';
  const idx = groupCommentKey.indexOf('::');
  return (idx >= 0 ? groupCommentKey.slice(0, idx) : groupCommentKey).trim();
}

export function isProductionCommentKey(groupCommentKey: string | undefined): boolean {
  if (!groupCommentKey) return false;
  const idx = groupCommentKey.indexOf('::');
  const name = idx >= 0 ? groupCommentKey.slice(idx + 2) : groupCommentKey;
  return isProductionGroupName(name);
}

function uniqueSorted(ids: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(ids).filter(Boolean))).sort();
}

function workTypeOf(project: Pick<RfqProject, 'workType'>): VendorWorkType {
  return project.workType === 'cnc_quote'
    ? 'cnc_quote'
    : project.workType === 'other_quote'
      ? 'other_quote'
      : 'timber_rfq';
}

export function collectApprovedSubmissionIds(
  project: Pick<RfqProject, 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>,
): Set<string> {
  const approvedIds = new Set<string>(project.approvedSubmissionIds ?? []);
  for (const ids of Object.values(project.approvedSubmissionIdsByRelease ?? {})) {
    for (const id of ids) {
      if (id) approvedIds.add(id);
    }
  }
  return approvedIds;
}

export function collectApprovedCncOrgIdsFromProject(
  project: Pick<
    RfqProject,
    'approvedSupplierOrgId' | 'approvedSupplierOrgIdByRelease' | 'approvedSupplierOrgIdsByRelease'
  >,
): string[] {
  const orgs = new Set<string>();
  if (project.approvedSupplierOrgId) orgs.add(project.approvedSupplierOrgId);
  for (const id of Object.values(project.approvedSupplierOrgIdByRelease ?? {})) {
    if (id) orgs.add(id);
  }
  for (const ids of Object.values(project.approvedSupplierOrgIdsByRelease ?? {})) {
    for (const id of ids) {
      if (id) orgs.add(id);
    }
  }
  return uniqueSorted(orgs);
}

export function collectAwardedTimberOrgIds(input: {
  projects: Array<Pick<RfqProject, 'workType' | 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>>;
  submissions: Array<Pick<RfqSubmission, 'id' | 'supplierOrgId'>>;
}): string[] {
  const approvedIds = new Set<string>();
  for (const project of input.projects) {
    if (workTypeOf(project) === 'cnc_quote') continue;
    for (const id of Array.from(collectApprovedSubmissionIds(project))) approvedIds.add(id);
  }
  if (!approvedIds.size) return [];
  const orgs = new Set<string>();
  for (const sub of input.submissions) {
    if (sub.id && approvedIds.has(sub.id) && sub.supplierOrgId) {
      orgs.add(sub.supplierOrgId);
    }
  }
  return uniqueSorted(orgs);
}

export function collectAwardedCncOrgIds(
  projects: Array<
    Pick<
      RfqProject,
      'workType' | 'approvedSupplierOrgId' | 'approvedSupplierOrgIdByRelease' | 'approvedSupplierOrgIdsByRelease'
    >
  >,
): string[] {
  const orgs = new Set<string>();
  for (const project of projects) {
    if (workTypeOf(project) !== 'cnc_quote') continue;
    for (const id of collectApprovedCncOrgIdsFromProject(project)) orgs.add(id);
  }
  return uniqueSorted(orgs);
}

export function resolveHubProjectForDeal(
  hubProjects: Array<Pick<TimberProject, 'id' | 'dealId' | 'phase'>>,
  dealId: string,
): Pick<TimberProject, 'id' | 'phase'> | undefined {
  const forDeal = hubProjects.filter(p => p.dealId === dealId && p.id);
  if (!forDeal.length) return undefined;
  return (
    forDeal.find(p => p.phase === 'contracted')
    || forDeal.find(p => p.phase !== 'preconstruction')
    || forDeal[0]
  );
}

export function dealIsUnderContract(
  hubProjects: Array<Pick<TimberProject, 'dealId' | 'phase'>>,
  dealId: string,
): boolean {
  return hubProjects.some(p => p.dealId === dealId && p.phase === 'contracted');
}

function uniqueSortedOrgIds(ids: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(ids).filter(Boolean))).sort();
}

/** Supplier org ids assigned on hub tasks / onboarding rows for this deal. */
export function collectHubTaskSupplierOrgIdsForDeal(input: {
  dealId: string;
  hubProjects: Array<Pick<TimberProject, 'id' | 'dealId'>>;
  projectTasks: Array<Pick<ProjectTask, 'projectId' | 'supplierOrgIds'>>;
  projectOnboardings: Array<{ projectId?: string; items?: Array<Pick<OnboardingItem, 'supplierOrgIds'>> }>;
}): string[] {
  const dealId = input.dealId.trim();
  const projectIds = new Set(
    input.hubProjects.filter((p) => p.dealId === dealId && p.id).map((p) => p.id!),
  );
  const orgs = new Set<string>();
  for (const t of input.projectTasks) {
    if (!projectIds.has(t.projectId)) continue;
    for (const id of t.supplierOrgIds ?? []) {
      if (id?.trim()) orgs.add(id.trim());
    }
  }
  for (const ob of input.projectOnboardings) {
    if (!ob.projectId || !projectIds.has(ob.projectId)) continue;
    for (const item of ob.items ?? []) {
      for (const id of item.supplierOrgIds ?? []) {
        if (id?.trim()) orgs.add(id.trim());
      }
    }
  }
  return uniqueSortedOrgIds(orgs);
}

export function buildProductionCommentAccessDoc(input: {
  dealId: string;
  projects: RfqProject[];
  submissions: Array<Pick<RfqSubmission, 'id' | 'supplierOrgId' | 'dealId' | 'rfqProjectId'>>;
  hubProjects?: Array<Pick<TimberProject, 'id' | 'dealId' | 'phase'>>;
  hubTaskSupplierOrgIds?: string[];
}): ProductionCommentAccess {
  const dealId = input.dealId.trim();
  const projects = input.projects.filter(p => p.dealId === dealId);
  const projectIds = new Set(projects.map(p => p.id).filter((id): id is string => !!id));
  const submissions = input.submissions.filter(
    s => s.dealId === dealId || (!!s.rfqProjectId && projectIds.has(s.rfqProjectId)),
  );
  const hub = input.hubProjects ? resolveHubProjectForDeal(input.hubProjects, dealId) : undefined;
  const timberOrgIds = uniqueSortedOrgIds([
    ...collectAwardedTimberOrgIds({ projects, submissions }),
    ...(input.hubTaskSupplierOrgIds ?? []),
  ]);
  return {
    dealId,
    timberOrgIds,
    cncOrgIds: collectAwardedCncOrgIds(projects),
    underContract: input.hubProjects ? dealIsUnderContract(input.hubProjects, dealId) : false,
    ...(hub?.id ? { hubProjectId: hub.id } : {}),
  };
}

export function productionCommentAccessHasAwards(
  access: Pick<ProductionCommentAccess, 'timberOrgIds' | 'cncOrgIds'>,
): boolean {
  return access.timberOrgIds.length > 0 || access.cncOrgIds.length > 0;
}

/**
 * Resolve work flags for award matching. Prefer the user's `supplierDesignations`;
 * when empty, fall back to the org's `capabilities` (invite default behavior).
 */
export function resolveSupplierWorkFlagsForMatch(input: {
  flags?: SupplierWorkFlag[] | unknown;
  orgCapabilities?: VendorCapability[] | unknown;
}): SupplierWorkFlag[] {
  const fromUser = parseSupplierWorkFlags(input.flags);
  if (fromUser.length > 0) return fromUser;
  if (!Array.isArray(input.orgCapabilities)) return [];
  return supplierWorkFlagsFromOrgCapabilities(input.orgCapabilities as VendorCapability[]);
}

export function supplierMatchesAwardedWork(input: {
  orgId?: string;
  flags?: SupplierWorkFlag[] | unknown;
  /** Used only when `flags` is empty — mirrors invite defaults from supplierOrgs.capabilities. */
  orgCapabilities?: VendorCapability[] | unknown;
  access?: Pick<ProductionCommentAccess, 'timberOrgIds' | 'cncOrgIds'> | null;
}): boolean {
  const orgId = input.orgId?.trim();
  const access = input.access;
  if (!orgId || !access) return false;
  const flags = resolveSupplierWorkFlagsForMatch({
    flags: input.flags,
    orgCapabilities: input.orgCapabilities,
  });
  if (flags.includes('timber_supplier') && access.timberOrgIds.includes(orgId)) return true;
  if (flags.includes('cnc_supplier') && access.cncOrgIds.includes(orgId)) return true;
  return false;
}

export function supplierCanAccessWorkTypeComments(input: {
  orgId?: string;
  flags?: SupplierWorkFlag[] | unknown;
  orgCapabilities?: VendorCapability[] | unknown;
  access?: Pick<ProductionCommentAccess, 'timberOrgIds' | 'cncOrgIds'> | null;
  workType?: VendorWorkType | 'timber' | 'cnc' | null;
}): boolean {
  const orgId = input.orgId?.trim();
  const access = input.access;
  if (!orgId || !access) return false;
  const flags = resolveSupplierWorkFlagsForMatch({
    flags: input.flags,
    orgCapabilities: input.orgCapabilities,
  });
  const isCnc = input.workType === 'cnc_quote' || input.workType === 'cnc';
  if (isCnc) {
    return flags.includes('cnc_supplier') && access.cncOrgIds.includes(orgId);
  }
  return flags.includes('timber_supplier') && access.timberOrgIds.includes(orgId);
}

export function buildSupplierProductionGroupContext(input: {
  dealId: string;
  hubProjectId?: string;
}): {
  groupKey: string;
  displayName: string;
  projectIds: string[];
  groupIds: string[];
  taskIds: string[];
  notifyUserIds: string[];
  primaryProjectId: string;
  primaryGroupId: string;
} {
  const dealId = input.dealId.trim();
  const projectId = (input.hubProjectId || dealId).trim();
  return {
    groupKey: productionCommentAccessId(dealId),
    displayName: 'Production',
    projectIds: projectId ? [projectId] : [],
    groupIds: [],
    taskIds: [],
    notifyUserIds: [],
    primaryProjectId: projectId,
    primaryGroupId: '',
  };
}
